/**
 * Дерево комментариев.
 *
 * В макете разметка комментария продублирована трижды — уровни c, c2, c3,
 * около двухсот строк повтора. Здесь один рекурсивный узел с ограничением
 * глубины: §14.1 разрешает три уровня, на третьем кнопка «Ответить» исчезает.
 */

import { Reply } from "lucide-react";
import { type ReactNode, useState } from "react";

import { Avatar } from "./Avatar";
import { Button } from "./Button";
import { cn } from "./cn";
import { CommentComposer } from "./CommentComposer";
import { IconButton } from "./IconButton";
import { AttachButton, PendingFiles } from "./PendingFiles";
import {
  docKey,
  fromPlain,
  isEmptyDoc,
  MAX_COMMENT_CHARS,
  plainText,
  type RichDoc,
} from "@taskflow/client-core";
import { RichTextEditor, RichTextView } from "./richtext/lazy";
import { type AttachOptions, usePendingFiles } from "./usePendingFiles";
import { Pencil, Trash2 } from "./motionIcons";
import { MAX_COMMENT_DEPTH } from "@taskflow/client-core/tasks";

// §14.1 — общее правило с приложением.
export { MAX_COMMENT_DEPTH };

export interface CommentAuthor {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
}

/** Правка файлов комментария: какие из уже приложенных автор убирает.
 *  Убранные снимаются только по «Сохранить» — «Отмена» их возвращает. */
export interface CommentFilesEdit {
  removed: ReadonlySet<string>;
  toggle: (fileId: string) => void;
  disabled: boolean;
}

export interface CommentNodeData {
  id: string;
  author: CommentAuthor;
  /** Пустой — у комментария из одних файлов. */
  text: string;
  /** Текст с оформлением; нет — показывается text абзацами. */
  body?: RichDoc | null;
  createdAtLabel: string;
  editedLabel?: string | null;
  canEdit: boolean;
  canDelete: boolean;
  /** Файлы комментария в готовом виде: как их показать, решает раздел задачи.
   *  Функция получает правку файлов, пока комментарий редактируется, иначе null. */
  attachments?: ReactNode | ((edit: CommentFilesEdit | null) => ReactNode);
  /** Сколько файлов уже приложено: без текста и без файлов комментарий не сохранить. */
  attachmentCount?: number;
  highlighted?: boolean;
  replies: CommentNodeData[];
}

interface CommentThreadProps {
  comments: CommentNodeData[];
  /** true — ответ отправлен, форма закрывается. */
  onReply: (
    parentId: string,
    text: string,
    files: File[],
    body: RichDoc | null,
  ) => Promise<boolean> | boolean;
  /** Файлы прикладываются к уже существующему комментарию — после правки;
   *  removedFileIds — уже приложенные файлы, которые автор убрал. */
  onEdit: (
    id: string,
    text: string,
    files: File[],
    removedFileIds: string[],
    body: RichDoc | null,
  ) => Promise<void> | void;
  onDeleteRequest: (id: string) => void;
  canComment: boolean;
  /** Разрешает прикладывать файлы к ответу. */
  attach?: AttachOptions;
  /** Идентификаторы комментариев, по которым идёт запрос. */
  busyIds?: Set<string>;
}

export function CommentThread({
  comments,
  onReply,
  onEdit,
  onDeleteRequest,
  canComment,
  attach,
  busyIds,
}: CommentThreadProps) {
  if (comments.length === 0) {
    return <p className="py-6 text-center text-[13px] text-faint">Комментариев пока нет</p>;
  }

  return (
    <ul className="flex flex-col gap-4">
      {comments.map((comment) => (
        <CommentNode
          key={comment.id}
          comment={comment}
          depth={1}
          onReply={onReply}
          onEdit={onEdit}
          onDeleteRequest={onDeleteRequest}
          canComment={canComment}
          {...(attach === undefined ? {} : { attach })}
          {...(busyIds === undefined ? {} : { busyIds })}
        />
      ))}
    </ul>
  );
}

interface CommentNodeProps {
  comment: CommentNodeData;
  depth: number;
  /** true — ответ отправлен, форма закрывается. */
  onReply: (
    parentId: string,
    text: string,
    files: File[],
    body: RichDoc | null,
  ) => Promise<boolean> | boolean;
  /** Файлы прикладываются к уже существующему комментарию — после правки;
   *  removedFileIds — уже приложенные файлы, которые автор убрал. */
  onEdit: (
    id: string,
    text: string,
    files: File[],
    removedFileIds: string[],
    body: RichDoc | null,
  ) => Promise<void> | void;
  onDeleteRequest: (id: string) => void;
  canComment: boolean;
  attach?: AttachOptions;
  busyIds?: Set<string>;
}

function CommentNode({
  comment,
  depth,
  onReply,
  onEdit,
  onDeleteRequest,
  canComment,
  attach,
  busyIds,
}: CommentNodeProps) {
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);
  // Документ комментария: прежние, без оформления, — абзацами из текста.
  const original = comment.body ?? (comment.text === "" ? null : fromPlain(comment.text));
  const [draft, setDraft] = useState<RichDoc | null>(original);
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const tray = usePendingFiles(attach);

  const busy = busyIds?.has(comment.id) === true || saving;
  const canReply = canComment && depth < MAX_COMMENT_DEPTH;

  // Без текста комментарий держится на файлах: убрать все и не оставить
  // текста значит получить невидимый комментарий — его удаляют целиком.
  const filesLeft = (comment.attachmentCount ?? 0) - removed.size + tray.files.length;
  const empty = isEmptyDoc(draft) && filesLeft <= 0;
  const tooLong = draft !== null && plainText(draft).length > MAX_COMMENT_CHARS;

  const toggleRemoved = (fileId: string) =>
    setRemoved((current) => {
      const next = new Set(current);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });

  const cancelEdit = () => {
    setDraft(original);
    setRemoved(new Set());
    tray.clear();
    setEditing(false);
  };

  const submitEdit = async () => {
    if (empty || tooLong || saving) return;
    const body = isEmptyDoc(draft) ? null : draft;
    const text = plainText(body).trim();
    // Правка без изменений не стоит запроса — просто закрыть поле.
    if (docKey(body) === docKey(original) && tray.files.length === 0 && removed.size === 0) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onEdit(comment.id, text, tray.files, [...removed], body);
    } finally {
      setSaving(false);
    }
    setRemoved(new Set());
    tray.clear();
    setEditing(false);
  };

  const attachments =
    typeof comment.attachments === "function"
      ? comment.attachments(
          editing ? { removed, toggle: toggleRemoved, disabled: busy } : null,
        )
      : comment.attachments;

  return (
    <li className="flex flex-col gap-2">
      <div id={`comment-${comment.id}`} className={cn("flex gap-2.5 scroll-m-4", comment.highlighted && "rounded-lg bg-[#98C983]/30 p-2 text-[#17251a]")}>
        <Avatar person={comment.author} size="sm" className="mt-0.5" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[13px] font-bold text-fg">
              {comment.author.lastName} {comment.author.firstName}
            </span>
            <span className="text-[11px] text-faint">{comment.createdAtLabel}</span>
            {comment.editedLabel !== null && comment.editedLabel !== undefined && (
              <span className="text-[11px] text-faint italic">{comment.editedLabel}</span>
            )}
          </div>

          {editing ? (
            <div className="mt-1.5 flex flex-col gap-2">
              <RichTextEditor
                value={original}
                onChange={setDraft}
                ariaLabel="Правка комментария"
                autoFocus
                size="sm"
                maxChars={MAX_COMMENT_CHARS}
                // Как и в новом комментарии: Ctrl+Enter (⌘+Enter) сохраняет.
                onSubmit={() => {
                  if (!busy) void submitEdit();
                  return true;
                }}
                {...(attach === undefined ? {} : { onPasteFiles: (files: File[]) => tray.add(files) })}
              />
              <PendingFiles
                files={tray.files}
                label="Файлы к комментарию"
                disabled={busy}
                onRemove={tray.remove}
              />
              {empty && (
                <p className="text-[11px] text-muted">
                  Оставьте текст или файл — пустой комментарий удаляется целиком.
                </p>
              )}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => void submitEdit()}
                  loading={busy}
                  disabled={empty || tooLong}
                >
                  Сохранить
                </Button>
                <Button size="sm" variant="secondary" onClick={cancelEdit}>
                  Отмена
                </Button>
                {attach && <AttachButton attach={attach} disabled={busy} onPick={tray.add} />}
              </div>
            </div>
          ) : (
            original !== null &&
            !isEmptyDoc(original) && (
              <div className="mt-0.5">
                <RichTextView doc={original} ariaLabel="Текст комментария" />
              </div>
            )
          )}

          {attachments}

          {!editing && (
            <div className="mt-1 flex items-center gap-0.5">
              {canReply && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Reply}
                  onClick={() => setReplying((value) => !value)}
                >
                  Ответить
                </Button>
              )}
              {comment.canEdit && (
                <IconButton
                  icon={Pencil}
                  label="Редактировать комментарий"
                  size="sm"
                  onClick={() => {
                    setDraft(original);
                    setEditing(true);
                  }}
                />
              )}
              {comment.canDelete && (
                <IconButton
                  icon={Trash2}
                  label="Удалить комментарий"
                  size="sm"
                  variant="danger"
                  onClick={() => onDeleteRequest(comment.id)}
                />
              )}
            </div>
          )}

          {replying && (
            <div className="mt-2">
              <CommentComposer
                autoFocus
                placeholder="Ваш ответ"
                {...(attach === undefined ? {} : { attach })}
                onCancel={() => setReplying(false)}
                onSubmit={async (text, files, body) => {
                  const sent = await onReply(comment.id, text, files, body);
                  if (sent) setReplying(false);
                  return sent;
                }}
              />
            </div>
          )}
        </div>
      </div>

      {comment.replies.length > 0 && (
        <ul
          className={cn(
            "ml-4 flex flex-col gap-4 border-l border-line pl-4",
            // Глубже третьего уровня отступ не растёт — иначе текст
            // на узком экране сжимается в столбик из одного слова.
            depth >= MAX_COMMENT_DEPTH - 1 && "ml-2 pl-3",
          )}
        >
          {comment.replies.map((reply) => (
            <CommentNode
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              onReply={onReply}
              onEdit={onEdit}
              onDeleteRequest={onDeleteRequest}
              canComment={canComment}
              {...(attach === undefined ? {} : { attach })}
              {...(busyIds === undefined ? {} : { busyIds })}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
